/**
 * Restaurant Dashboard API Clients (centralized)
 *
 * Wichtig: Das Dashboard soll KEINE eigenen API.graphql Wrapper pflegen.
 * Stattdessen delegieren wir an die zentralen Clients in `@tasty/api`.
 *
 * Diese Datei bietet eine Kompatibilitätsschicht für Legacy-Methodennamen
 * (z.B. fetchOrders / fetchOrderByID / observeOrders), damit die UI (inkl. Tailwind/Vite)
 * ohne invasive Refactors stabil bleibt.
 *
 * Die zentralen Clients in `@tasty/api` sind bewusst schmal gehalten (read-first).
 * Die vom Dashboard zusätzlich benötigten CRUD-/Subscription-Methoden werden HIER
 * on top implementiert - auf Basis der von `@tasty/api` exportierten Roh-Operationen
 * (queries/mutations/subscriptions) und der Request-Helper aus `@tasty/api/client`.
 */

import {
  AppConfigClient,
  BusinessHourRestaurantClient,
  DishCategoryClient,
  DishClient,
  DishOptionClient,
  DishOptionItemClient,
  InvoiceClient,
  OrderClient,
  OrderDishClient,
  RestaurantCategoryClient,
  RestaurantClient,
  UserClient,
} from "@tasty/api";

import {
  graphqlRequest,
  graphqlMutation,
  graphqlSubscription,
  removeTypename,
  filterDeletedItem,
  filterQueryDeletedList,
} from "@tasty/api/client";

import * as queries from "@tasty/api/graphql/queries";
import * as mutations from "@tasty/api/graphql/mutations";
import * as subscriptions from "@tasty/api/graphql/subscriptions";

import type {
  Dish,
  GetDishQuery,
  ListDishesQuery,
  ListDishesQueryVariables,
  CreateDishInput,
  CreateDishMutation,
  UpdateDishInput,
  UpdateDishMutation,
  DeleteDishInput,
  DeleteDishMutation,
  OnUpdateDishSubscription,
  OnUpdateDishSubscriptionVariables,
  DishCategory,
  CreateDishCategoryInput,
  CreateDishCategoryMutation,
  UpdateDishCategoryInput,
  UpdateDishCategoryMutation,
  DeleteDishCategoryInput,
  DeleteDishCategoryMutation,
  GetDishCategoryQuery,
  DishOption,
  CreateDishOptionInput,
  CreateDishOptionMutation,
  UpdateDishOptionInput,
  UpdateDishOptionMutation,
  GetDishOptionQuery,
  BusinessHourRestaurant,
  CreateBusinessHourRestaurantInput,
  CreateBusinessHourRestaurantMutation,
  UpdateBusinessHourRestaurantInput,
  UpdateBusinessHourRestaurantMutation,
  DeleteBusinessHourRestaurantInput,
  DeleteBusinessHourRestaurantMutation,
  GetBusinessHourRestaurantQuery,
  Restaurant,
  ListRestaurantsQuery,
  ListRestaurantsQueryVariables,
  Order,
  ListOrdersQuery,
  ListOrdersQueryVariables,
  RestaurantCategory,
  UpdateRestaurantCategoryInput,
  UpdateRestaurantCategoryMutation,
  GetRestaurantCategoryQuery,
  OnUpdateRestaurantCategorySubscription,
  OnUpdateRestaurantCategorySubscriptionVariables,
} from "@tasty/api";

// -------------------- Extended Clients (Dashboard-only CRUD) --------------------
//
// Die folgenden Clients erweitern die schmalen `@tasty/api`-Clients um die
// Methoden, die das Dashboard braucht (createItem/deleteItem/... etc.).
// Muster kopiert aus @tasty/api/src/client/*.ts (updateItem/queryItem).

/** Dish: create/delete/restore/queryDeletedList/subscribe zusätzlich */
const DishClientExtended = {
  ...DishClient,

  async createItem(input: CreateDishInput): Promise<Dish | null> {
    const result = await graphqlMutation<CreateDishMutation, CreateDishInput>(
      mutations.createDish,
      input,
    );
    return result ? (result.createDish as Dish) : null;
  },

  async deleteItem(input: DeleteDishInput): Promise<void> {
    const original = await DishClient.queryItem(input.id);
    if (!original) return;

    await graphqlMutation<DeleteDishMutation, DeleteDishInput>(
      mutations.deleteDish,
      { ...input, _version: original._version },
    );
  },

  async restoreItem(input: UpdateDishInput): Promise<Dish | null> {
    const cleanInput = removeTypename({ ...input, _deleted: null });
    const result = await graphqlMutation<UpdateDishMutation, UpdateDishInput>(
      mutations.updateDish,
      cleanInput as UpdateDishInput,
    );
    return result ? (filterDeletedItem(result.updateDish) as Dish | null) : null;
  },

  /** Liste aller soft-deleted Dishes (inverse zu queryList) */
  async queryDeletedList(
    variables: ListDishesQueryVariables = {},
  ): Promise<Dish[]> {
    const result = await graphqlRequest<
      ListDishesQuery,
      ListDishesQueryVariables
    >(queries.listDishes, variables);
    const items = (result?.listDishes?.items ?? []).filter(
      (item): item is NonNullable<typeof item> =>
        item != null && item._deleted === true,
    );
    return items as unknown as Dish[];
  },

  subscribe(variables?: OnUpdateDishSubscriptionVariables) {
    return graphqlSubscription<
      OnUpdateDishSubscription,
      OnUpdateDishSubscriptionVariables
    >(subscriptions.onUpdateDish, variables);
  },
};

/** DishCategory: create/update/delete zusätzlich */
const DishCategoryClientExtended = {
  ...DishCategoryClient,

  async createItem(
    input: CreateDishCategoryInput,
  ): Promise<DishCategory | null> {
    const result = await graphqlMutation<
      CreateDishCategoryMutation,
      CreateDishCategoryInput
    >(mutations.createDishCategory, input);
    return result ? (result.createDishCategory as DishCategory) : null;
  },

  async updateItem(
    input: UpdateDishCategoryInput,
  ): Promise<DishCategory | null> {
    const original = await DishCategoryClient.queryItem({ id: input.id });
    if (!original) return null;

    const cleanInput = removeTypename({
      ...input,
      _version: original._version,
    });
    const result = await graphqlMutation<
      UpdateDishCategoryMutation,
      UpdateDishCategoryInput
    >(mutations.updateDishCategory, cleanInput as UpdateDishCategoryInput);
    return result
      ? (filterDeletedItem(result.updateDishCategory) as DishCategory | null)
      : null;
  },

  async deleteItem(input: DeleteDishCategoryInput): Promise<void> {
    const original = await DishCategoryClient.queryItem({ id: input.id });
    if (!original) return;

    await graphqlMutation<
      DeleteDishCategoryMutation,
      DeleteDishCategoryInput
    >(mutations.deleteDishCategory, { ...input, _version: original._version });
  },
};

/** DishOption: create/update zusätzlich */
const DishOptionClientExtended = {
  ...DishOptionClient,

  async createItem(input: CreateDishOptionInput): Promise<DishOption | null> {
    const result = await graphqlMutation<
      CreateDishOptionMutation,
      CreateDishOptionInput
    >(mutations.createDishOption, input);
    return result ? (result.createDishOption as DishOption) : null;
  },

  async updateItem(input: UpdateDishOptionInput): Promise<DishOption | null> {
    const original = await DishOptionClient.queryItem({ id: input.id });
    if (!original) return null;

    const cleanInput = removeTypename({
      ...input,
      _version: original._version,
    });
    const result = await graphqlMutation<
      UpdateDishOptionMutation,
      UpdateDishOptionInput
    >(mutations.updateDishOption, cleanInput as UpdateDishOptionInput);
    return result
      ? (filterDeletedItem(result.updateDishOption) as DishOption | null)
      : null;
  },
};

/** BusinessHourRestaurant: create/update/delete zusätzlich */
const BusinessHourRestaurantClientExtended = {
  ...BusinessHourRestaurantClient,

  async createItem(
    input: CreateBusinessHourRestaurantInput,
  ): Promise<BusinessHourRestaurant | null> {
    const result = await graphqlMutation<
      CreateBusinessHourRestaurantMutation,
      CreateBusinessHourRestaurantInput
    >(mutations.createBusinessHourRestaurant, input);
    return result
      ? (result.createBusinessHourRestaurant as BusinessHourRestaurant)
      : null;
  },

  async updateItem(
    input: UpdateBusinessHourRestaurantInput,
  ): Promise<BusinessHourRestaurant | null> {
    const original = await BusinessHourRestaurantClient.queryItem({
      id: input.id,
    });
    if (!original) return null;

    const cleanInput = removeTypename({
      ...input,
      _version: original._version,
    });
    const result = await graphqlMutation<
      UpdateBusinessHourRestaurantMutation,
      UpdateBusinessHourRestaurantInput
    >(
      mutations.updateBusinessHourRestaurant,
      cleanInput as UpdateBusinessHourRestaurantInput,
    );
    return result
      ? (filterDeletedItem(
          result.updateBusinessHourRestaurant,
        ) as BusinessHourRestaurant | null)
      : null;
  },

  async deleteItem(input: DeleteBusinessHourRestaurantInput): Promise<void> {
    const original = await BusinessHourRestaurantClient.queryItem({
      id: input.id,
    });
    if (!original) return;

    await graphqlMutation<
      DeleteBusinessHourRestaurantMutation,
      DeleteBusinessHourRestaurantInput
    >(mutations.deleteBusinessHourRestaurant, {
      ...input,
      _version: original._version,
    });
  },
};

/** Restaurant: queryByAdminSub zusätzlich (Filter auf adminSub via listRestaurants) */
const RestaurantClientExtended = {
  ...RestaurantClient,

  async queryByAdminSub(adminSub: string): Promise<Restaurant | null> {
    // DynamoDB wendet den Filter NACH dem limit an -> mit limit:1 findet man
    // den Datensatz fast nie. Deshalb seitenweise scannen (limit hoch) und
    // nextToken folgen, bis ein Treffer kommt oder nichts mehr da ist.
    let nextToken: string | null | undefined = undefined;
    do {
      const result: ListRestaurantsQuery | null = await graphqlRequest<
        ListRestaurantsQuery,
        ListRestaurantsQueryVariables
      >(queries.listRestaurants, {
        filter: { adminSub: { eq: adminSub } },
        limit: 1000,
        nextToken,
      });
      if (!result) return null;
      const items = filterQueryDeletedList<Restaurant>(result.listRestaurants);
      if (items.length > 0) return items[0];
      nextToken = result.listRestaurants?.nextToken;
    } while (nextToken);
    return null;
  },
};

/** Order: queryListPaginated zusätzlich (sammelt alle Seiten via nextToken) */
const OrderClientExtended = {
  ...OrderClient,

  async queryListPaginated(
    variables: ListOrdersQueryVariables = {},
  ): Promise<Order[]> {
    let allItems: Order[] = [];
    let nextToken: string | null = null;

    do {
      const result: ListOrdersQuery | null = await graphqlRequest<
        ListOrdersQuery,
        ListOrdersQueryVariables
      >(queries.listOrders, { ...variables, nextToken } as ListOrdersQueryVariables);
      if (!result?.listOrders) break;

      allItems = allItems.concat(
        filterQueryDeletedList<Order>(result.listOrders),
      );
      nextToken = result.listOrders.nextToken ?? null;
    } while (nextToken);

    return allItems;
  },
};

/** RestaurantCategory: updateItem + subscribe zusätzlich */
const RestaurantCategoryClientExtended = {
  ...RestaurantCategoryClient,

  async updateItem(
    input: UpdateRestaurantCategoryInput,
  ): Promise<RestaurantCategory | null> {
    const original = await RestaurantCategoryClient.queryItem({ id: input.id });
    if (!original) return null;

    const cleanInput = removeTypename({
      ...input,
      _version: original._version,
    });
    const result = await graphqlMutation<
      UpdateRestaurantCategoryMutation,
      UpdateRestaurantCategoryInput
    >(
      mutations.updateRestaurantCategory,
      cleanInput as UpdateRestaurantCategoryInput,
    );
    return result
      ? (filterDeletedItem(
          result.updateRestaurantCategory,
        ) as RestaurantCategory | null)
      : null;
  },

  subscribe(variables?: OnUpdateRestaurantCategorySubscriptionVariables) {
    return graphqlSubscription<
      OnUpdateRestaurantCategorySubscription,
      OnUpdateRestaurantCategorySubscriptionVariables
    >(subscriptions.onUpdateRestaurantCategory, variables);
  },
};

// Re-export erweiterte Clients unter den erwarteten Namen
export {
  AppConfigClient,
  DishOptionItemClient,
  InvoiceClient,
  OrderDishClient,
  UserClient,
  mutations,
  queries,
  subscriptions,
  removeTypename,
};

export {
  BusinessHourRestaurantClientExtended as BusinessHourRestaurantClient,
  DishClientExtended as DishClient,
  DishCategoryClientExtended as DishCategoryClient,
  DishOptionClientExtended as DishOptionClient,
  RestaurantClientExtended as RestaurantClient,
  OrderClientExtended as OrderClient,
  RestaurantCategoryClientExtended as RestaurantCategoryClient,
};

// -------------------- Compatibility API Objects --------------------

export const AppConfigAPI = {
  ...AppConfigClient,
  fetchAppConfig: AppConfigClient.queryItem,
  observeAppConfig: AppConfigClient.subscribeToUpdates,
};

export const BusinessHoursRestaurantAPI = {
  ...BusinessHourRestaurantClientExtended,
  queryItem: BusinessHourRestaurantClientExtended.queryItem,
  queryList: BusinessHourRestaurantClientExtended.queryList,
  createItem: BusinessHourRestaurantClientExtended.createItem,
  updateItem: BusinessHourRestaurantClientExtended.updateItem,
  deleteItem: BusinessHourRestaurantClientExtended.deleteItem,
  updateSubscription: BusinessHourRestaurantClientExtended.subscribeToUpdates,
};

export const DishAPI = {
  ...DishClientExtended,
  fetchDishByID: DishClientExtended.queryItem,
  queryList: DishClientExtended.queryList,
  queryDeletedList: DishClientExtended.queryDeletedList,
  createItem: DishClientExtended.createItem,
  updateItem: DishClientExtended.updateItem,
  updateDishByID: DishClientExtended.updateItem,
  deleteItem: DishClientExtended.deleteItem,
  restoreItem: DishClientExtended.restoreItem,
  observeDishes: DishClientExtended.subscribe,
};

export const DishCategoryAPI = {
  ...DishCategoryClientExtended,
  queryList: DishCategoryClientExtended.queryList,
  fetchListByRestaurantID: DishCategoryClientExtended.queryList,
  createItem: DishCategoryClientExtended.createItem,
  deleteItem: DishCategoryClientExtended.deleteItem,
  updateItem: DishCategoryClientExtended.updateItem,
  updateDishCategoryByID: DishCategoryClientExtended.updateItem,
};

export const DishOptionAPI = {
  ...DishOptionClientExtended,
};

export const DishOptionItemAPI = {
  ...DishOptionItemClient,
};

export const OrderAPI = {
  ...OrderClientExtended,
  // legacy names (Tailwind/Vite pages + legacy screens)
  fetchItem: OrderClientExtended.queryItem,
  fetchOrderByID: OrderClientExtended.queryItem,
  queryItem: OrderClientExtended.queryItem,
  fetchList: OrderClientExtended.queryListPaginated,
  fetchOrders: OrderClientExtended.queryListPaginated,
  queryListPaginated: OrderClientExtended.queryListPaginated,
  updateItem: OrderClientExtended.updateItem,
  updateOrderByID: OrderClientExtended.updateItem,
  subscribeToUpdates: OrderClientExtended.subscribeToUpdates,
  observeOrders: OrderClientExtended.subscribeToUpdates,
};

export const OrderDishAPI = {
  ...OrderDishClient,
  queryItem: OrderDishClient.queryItem,
  queryList: OrderDishClient.queryList,
  queryListByOrderID: OrderDishClient.queryListByOrderID,
  createItem: OrderDishClient.createItem,
  updateItem: OrderDishClient.updateItem,
  deleteItem: OrderDishClient.deleteItem,
};

export const RestaurantAPI = {
  ...RestaurantClientExtended,
  fetchRestaurantByID: RestaurantClientExtended.queryItem,
  fetchRestaurantByAdminID: RestaurantClientExtended.queryByAdminSub,
  createItem: RestaurantClientExtended.createItem,
  updateItem: RestaurantClientExtended.updateItem,
  updateRestaurantByID: RestaurantClientExtended.updateItem,
  observeRestaurantes: RestaurantClientExtended.subscribeToUpdates,
  subscribeToUpdates: RestaurantClientExtended.subscribeToUpdates,
};

export const RestaurantCategoryAPI = {
  ...RestaurantCategoryClientExtended,
  fetchRestaurantCategoryByID: RestaurantCategoryClientExtended.queryItem,
  fetchListByRestaurantID: RestaurantCategoryClientExtended.queryList,
  queryItem: RestaurantCategoryClientExtended.queryItem,
  queryList: RestaurantCategoryClientExtended.queryList,
  createItem: RestaurantCategoryClientExtended.createItem,
  updateItem: RestaurantCategoryClientExtended.updateItem,
  updateRestaurantCategoryByID: RestaurantCategoryClientExtended.updateItem,
  deleteItem: RestaurantCategoryClientExtended.deleteItem,
  observeRestaurantCategories: RestaurantCategoryClientExtended.subscribe,
  subscribe: RestaurantCategoryClientExtended.subscribe,
};

export const UserAPI = {
  ...UserClient,
  queryItem: UserClient.queryItem,
  queryList: UserClient.queryList,
  fetchUserByID: UserClient.queryItem,
  fetchList: UserClient.queryList,
  create: UserClient.createItem,
  update: UserClient.updateItem,
  deleteByID: UserClient.deleteItem,
};

export const InvoiceAPI = {
  ...InvoiceClient,
  queryInvoiceListByCustomerID: InvoiceClient.queryListByCustomerID,
};
